import { useStreakQuery, type StreakMilestoneDto } from '@/api/hooks'
import { GoldIcon, WoodIcon, PrestigeIcon, LifebuoyIcon } from '@/components/game/GameIcons'

/**
 * Carte « Ta carte au trésor » du tableau de bord — rendu « CARTE MARINE SOMBRE »
 * (option 2 validée sur maquette) : même esthétique que la carte du monde du jeu.
 * Un panneau SVG pleine largeur figure une carte marine ; chaque jour actif y
 * trace une CROIX ROUGE le long d'une route pointillée dorée jusqu'au TRÉSOR du
 * palier visé (coffre doré cerclé).
 *
 * La frise est PAR SÉRIE : elle ne montre pas une fenêtre glissante des 7 derniers
 * jours mais le segment courant — du palier précédent au prochain — avec les croix
 * DÉJÀ tracées ET celles qu'il reste à cocher jusqu'au coffre. À chaque palier
 * franchi, la frise repart donc à zéro vers l'objectif suivant.
 *
 * Le jour où un coffre de palier tombe (`reachedMilestone`), la carte le CÉLÈBRE :
 * le coffre grossit, s'auréole et tressaute doucement (motion-safe — respecte
 * prefers-reduced-motion). Le grand compteur « N jours de suite », lui, continue
 * de grimper (le remettre à zéro casserait l'échelle des paliers).
 */

// Palette (teintes réelles de tailwind.config.js — pas de teinte fantôme) :
const SAND_300 = '#E5CC8F'
const SAND_100 = '#F5EDD7'
const OCEAN_300 = '#5FBDD0'
// Rouge BRIQUE des croix — volontairement distinct du text-red-400 d'alerte.
const BRICK = '#d9584a'

/** Ordinal français court : 1ᵉʳ, puis Nᵉ (3ᵉ, 7ᵉ, 14ᵉ, 30ᵉ, 45ᵉ…). */
export function frenchOrdinal(day: number): string {
  return 1 === day ? '1ᵉʳ' : `${day}ᵉ`
}

interface Cross {
  day: number
  state: 'past' | 'today' | 'upcoming'
}

/**
 * Les croix du SEGMENT courant : de `segmentStartDay + 1` (jour suivant le palier
 * précédent) jusqu'à la veille du coffre affiché (`focusDay - 1`). On garde le jour
 * courant visible et on plafonne à 7 croix ; `hiddenBefore`/`gap` signalent des
 * jours masqués avant la fenêtre (série longue déjà entamée) ou après elle (encore
 * loin du coffre), rendus par une ellipse « ⋯ ».
 */
export function buildCrosses(p: {
  count: number
  todayDone: boolean
  focusDay: number
  segmentStartDay: number
  celebrating: boolean
}): {
  crosses: Cross[]
  gap: boolean
  hiddenBefore: boolean
  chestIsToday: boolean
} {
  // Le jour « courant » : celui décroché aujourd'hui si validé, sinon celui qui
  // attend la première action. En célébration, il coïncide avec le coffre (il n'y
  // a donc pas de croix « aujourd'hui » séparée — le coffre EST aujourd'hui).
  const currentDay = p.celebrating ? p.count : p.todayDone ? p.count : p.count + 1
  const chestIsToday = p.celebrating || currentDay === p.focusDay

  const firstDay = p.segmentStartDay + 1
  const lastCrossDay = p.focusDay - 1
  if (lastCrossDay < firstDay) {
    // Segment sans croix intermédiaire (ex. 1ᵉʳ jour) : le coffre seul.
    return { crosses: [], gap: false, hiddenBefore: false, chestIsToday }
  }

  const windowEnd = Math.min(lastCrossDay, Math.max(currentDay, firstDay + 6))
  const windowStart = Math.max(firstDay, windowEnd - 6)

  const crosses: Cross[] = []
  for (let day = windowStart; day <= windowEnd; day++) {
    crosses.push({
      day,
      state: day === currentDay ? 'today' : day <= p.count ? 'past' : 'upcoming',
    })
  }

  return {
    crosses,
    gap: windowEnd < lastCrossDay,
    hiddenBefore: windowStart > firstDay,
    chestIsToday,
  }
}

// ——— géométrie du panneau carte marine (viewBox fixe, rendu responsive) ———
const VIEW_W = 640
const VIEW_H = 130
const ROUTE_Y = 74
const WAVE = 7
const ROUTE_START_X = 28
const TREASURE_X = 540
const SLOT_MAX_X = 460
const ELLIPSIS_X = 46
const GAP_ELLIPSIS_X = 500
const LABEL_Y = ROUTE_Y + 30

/**
 * Abscisses des jours du cycle : CALCULÉES et réparties uniformément sur la
 * largeur disponible (aucune position en dur dépendant du nombre de jours).
 */
function slotXs(n: number, hasEllipsis: boolean): number[] {
  if (n === 0) return []
  const min = hasEllipsis ? 74 : 32
  if (n === 1) return [(min + SLOT_MAX_X) / 2]
  return Array.from({ length: n }, (_, i) => min + (i * (SLOT_MAX_X - min)) / (n - 1))
}

function slotY(i: number): number {
  return ROUTE_Y + (i % 2 === 0 ? -WAVE : WAVE)
}

/** Chemin ondulé passant par tous les points (cubiques à tangentes horizontales). */
function routePath(points: { x: number; y: number }[]): string {
  if (points.length === 0) return ''
  let d = `M ${points[0].x} ${points[0].y}`
  for (let i = 1; i < points.length; i++) {
    const p = points[i - 1]
    const q = points[i]
    const mx = (p.x + q.x) / 2
    d += ` C ${mx} ${p.y}, ${mx} ${q.y}, ${q.x} ${q.y}`
  }
  return d
}

/** Croix en X (deux traits croisés, linecap round). */
function crossLines(x: number, y: number, size: number) {
  return [
    { x1: x - size, y1: y - size, x2: x + size, y2: y + size },
    { x1: x - size, y1: y + size, x2: x + size, y2: y - size },
  ]
}

/** Petite étoile à 4 branches (étincelle de célébration). */
function sparkPath(cx: number, cy: number, r: number): string {
  const t = r * 0.28
  return `M ${cx} ${cy - r} L ${cx + t} ${cy - t} L ${cx + r} ${cy} L ${cx + t} ${cy + t} L ${cx} ${cy + r} L ${cx - t} ${cy + t} L ${cx - r} ${cy} L ${cx - t} ${cy - t} Z`
}

export function StreakCard() {
  const { data } = useStreakQuery()
  if (!data) return null

  const { count, todayDone, shoreLeaves, nextMilestone, reachedMilestone, segmentStartDay } = data

  // Coffre AFFICHÉ : le palier décroché aujourd'hui (célébré) sinon le prochain.
  const focus: StreakMilestoneDto = reachedMilestone ?? nextMilestone
  const celebrating = reachedMilestone != null

  const { crosses, gap, hiddenBefore, chestIsToday } = buildCrosses({
    count,
    todayDone,
    focusDay: focus.day,
    segmentStartDay,
    celebrating,
  })

  // Emplacements des jours + points d'ancrage de la route (bord à bord).
  const xs = slotXs(crosses.length, hiddenBefore)
  const slotPoints = crosses.map((_, i) => ({ x: xs[i], y: slotY(i) }))
  // Sans croix (viser directement le coffre du jour), la route ondule quand même.
  const wavePoints =
    crosses.length === 0
      ? [
          { x: 200, y: ROUTE_Y - WAVE },
          { x: 380, y: ROUTE_Y + WAVE },
        ]
      : []
  const points = [
    { x: ROUTE_START_X, y: ROUTE_Y },
    ...(hiddenBefore ? [{ x: ELLIPSIS_X, y: ROUTE_Y }] : []),
    ...slotPoints,
    ...(gap ? [{ x: GAP_ELLIPSIS_X, y: ROUTE_Y }] : []),
    ...wavePoints,
    { x: TREASURE_X, y: ROUTE_Y },
  ]

  const m = focus
  const pulse = 'motion-safe:animate-pulse'
  const treasurePending = chestIsToday && !todayDone

  return (
    <section
      data-testid="streak-card"
      data-celebrating={celebrating ? 'true' : 'false'}
      className="rounded-2xl border border-sand-500/20 bg-ocean-900/55 p-5 sm:p-6"
    >
      {/* Célébration du coffre décroché — motion-safe (respecte prefers-reduced-motion). */}
      <style>{`
        @keyframes streakChestWon { 0%,100%{transform:scale(1.12) rotate(0deg)} 30%{transform:scale(1.12) rotate(-3.5deg)} 65%{transform:scale(1.12) rotate(2.5deg)} }
        @keyframes streakHalo { 0%,100%{opacity:.24} 50%{opacity:.55} }
        @keyframes streakSpark { 0%,100%{opacity:0; transform:scale(.55)} 50%{opacity:1; transform:scale(1)} }
        .streak-chest-won { animation: streakChestWon 1.15s ease-in-out infinite; transform-box: fill-box; transform-origin: center bottom; }
        .streak-halo { animation: streakHalo 1.9s ease-in-out infinite; }
        .streak-spark { transform-box: fill-box; transform-origin: center; }
        .streak-spark-a { animation: streakSpark 1.35s ease-in-out infinite; }
        .streak-spark-b { animation: streakSpark 1.35s ease-in-out .55s infinite; }
        @media (prefers-reduced-motion: reduce) {
          .streak-chest-won { animation: none; transform: scale(1.12); }
          .streak-halo { animation: none; opacity: .3; }
          .streak-spark-a, .streak-spark-b { animation: none; opacity: .85; }
        }
      `}</style>

      <p className="text-[11px] uppercase tracking-[0.32em] text-ocean-300">Ta carte au trésor</p>
      <p className="font-display text-3xl sm:text-4xl text-sand-100 tabular-nums mt-1.5">
        <span className="text-sand-300">{count}</span> jour{count > 1 ? 's' : ''} de suite
      </p>

      {/* ——— le panneau carte marine (SVG responsive pleine largeur) ——— */}
      <svg
        viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
        className="block w-full h-auto mt-3"
        role="img"
        aria-label={
          celebrating
            ? `Carte au trésor : trésor du ${frenchOrdinal(m.day)} jour décroché, ${count} jours de suite`
            : `Carte au trésor : ${count} jour${count > 1 ? 's' : ''} de suite, trésor du ${frenchOrdinal(m.day)} jour`
        }
      >
        {/* fond océan profond + bord */}
        <rect x={0.5} y={0.5} width={VIEW_W - 1} height={VIEW_H - 1} rx={10} fill="#072B37" stroke="rgba(95,189,208,.25)" strokeWidth={1} />

        {/* quadrillage de carte marine, discret */}
        <g aria-hidden="true" stroke="rgba(95,189,208,.09)" strokeWidth={1}>
          {[80, 160, 240, 320, 400, 480, 560].map((x) => (
            <line key={x} x1={x} y1={1.5} x2={x} y2={VIEW_H - 1.5} />
          ))}
          {[32, 65, 98].map((y) => (
            <line key={y} x1={1.5} y1={y} x2={VIEW_W - 1.5} y2={y} />
          ))}
        </g>

        {/* sondes (profondeurs) réparties sur la largeur */}
        <g aria-hidden="true" fill="rgba(191,228,237,.25)" fontFamily="Georgia, serif" fontStyle="italic" fontSize={9}>
          <text x={96} y={28}>12</text>
          <text x={318} y={112}>7</text>
          <text x={588} y={30}>15</text>
        </g>

        {/* bouts de côte esquissés (gauche-bas, droite-haut) */}
        <g aria-hidden="true" fill="none" stroke="rgba(95,189,208,.3)" strokeWidth={1.2} strokeLinecap="round">
          <path d="M 6 116 Q 42 100 78 110 Q 112 119 150 108" />
          <path d="M 468 14 Q 512 27 556 16 Q 598 7 634 19" />
        </g>

        {/* la route pointillée dorée, de bord à bord jusqu'au trésor */}
        <path
          d={routePath(points)}
          fill="none"
          stroke="rgba(229,204,143,.5)"
          strokeWidth={1.8}
          strokeDasharray="2 8"
          strokeLinecap="round"
        />

        {/* jours masqués AVANT la fenêtre (série longue déjà entamée) */}
        {hiddenBefore && (
          <g data-testid="streak-ellipsis" fill={SAND_300} opacity={0.5}>
            <circle cx={ELLIPSIS_X - 8} cy={ROUTE_Y} r={1.6} />
            <circle cx={ELLIPSIS_X} cy={ROUTE_Y} r={1.6} />
            <circle cx={ELLIPSIS_X + 8} cy={ROUTE_Y} r={1.6} />
          </g>
        )}

        {/* jours masqués APRÈS la fenêtre, avant le coffre (encore loin de l'objectif) */}
        {gap && (
          <g data-testid="streak-ellipsis-gap" fill={SAND_300} opacity={0.5}>
            <circle cx={GAP_ELLIPSIS_X - 8} cy={ROUTE_Y} r={1.6} />
            <circle cx={GAP_ELLIPSIS_X} cy={ROUTE_Y} r={1.6} />
            <circle cx={GAP_ELLIPSIS_X + 8} cy={ROUTE_Y} r={1.6} />
          </g>
        )}

        {/* croix du segment courant, réparties uniformément le long de la route */}
        {crosses.map((c, i) => {
          const x = xs[i]
          const y = slotY(i)
          if (c.state === 'today') {
            const lines = crossLines(x, y, 7)
            return (
              <g key={c.day} data-testid="streak-cross-today" data-done={todayDone ? 'true' : 'false'} data-x={x}>
                {todayDone ? (
                  lines.map((l, j) => <line key={j} {...l} stroke={BRICK} strokeWidth={3} strokeLinecap="round" />)
                ) : (
                  <g className={pulse}>
                    {lines.map((l, j) => (
                      <line key={j} {...l} stroke={SAND_300} strokeWidth={2.6} strokeDasharray="3 4" strokeLinecap="round" />
                    ))}
                  </g>
                )}
                <text x={x} y={LABEL_Y} textAnchor="middle" fill={OCEAN_300} fontSize={9} fontStyle="italic">
                  aujourd'hui
                </text>
              </g>
            )
          }
          if (c.state === 'past') {
            return (
              <g key={c.day} data-testid="streak-cross-past" data-x={x}>
                {crossLines(x, y, 6).map((l, j) => (
                  <line key={j} {...l} stroke={BRICK} strokeWidth={3} strokeLinecap="round" />
                ))}
              </g>
            )
          }
          // upcoming : croix creuse et pâle — un jour qu'il RESTE à cocher vers le coffre.
          return (
            <g key={c.day} data-testid="streak-day-upcoming" data-x={x} opacity={0.5}>
              {crossLines(x, y, 5).map((l, j) => (
                <line key={j} {...l} stroke="rgba(191,228,237,.65)" strokeWidth={1.6} strokeLinecap="round" />
              ))}
            </g>
          )
        })}

        {/* Le trésor du palier visé : COFFRE doré cerclé. Décroché aujourd'hui, il
            grossit, s'auréole et tressaute ; sinon il pulse s'il est à portée du jour. */}
        <g data-testid="streak-treasure" data-x={TREASURE_X} data-celebrating={celebrating ? 'true' : 'false'}>
          <text
            x={TREASURE_X}
            y={46}
            textAnchor="middle"
            fill={SAND_300}
            fontSize={10.5}
            fontFamily="Georgia, serif"
            className="tabular-nums"
          >
            {frenchOrdinal(m.day)} jour
          </text>
          <g transform={`translate(${TREASURE_X}, ${ROUTE_Y})`}>
            {celebrating && <circle className="streak-halo" cx={0} cy={0} r={23} fill={SAND_300} />}
            <g className={treasurePending ? pulse : undefined}>
              <circle
                cx={0}
                cy={0}
                r={16}
                fill="none"
                stroke={SAND_300}
                strokeWidth={celebrating ? 2 : 1.6}
                strokeDasharray={treasurePending ? '4 4' : undefined}
              />
            </g>
            <g data-testid="streak-treasure-chest" className={celebrating ? 'streak-chest-won' : undefined}>
              {/* Coffre cerclé (candidat A validé) — jonction couvercle/corps, deux
                  cerclages verticaux, moraillon à serrure. */}
              <path
                d="M -8.5 -2 L -8.5 -4.2 Q -8.5 -8.6 -4 -8.6 L 4 -8.6 Q 8.5 -8.6 8.5 -4.2 L 8.5 -2 Z"
                fill="rgba(229,204,143,.26)"
                stroke={SAND_300}
                strokeWidth={1.3}
                strokeLinejoin="round"
              />
              <rect x={-8.5} y={-2} width={17} height={9.2} rx={1.2} fill="rgba(229,204,143,.10)" stroke={SAND_300} strokeWidth={1.3} />
              <line x1={-4.8} y1={-8.3} x2={-4.8} y2={7} stroke={SAND_300} strokeWidth={1.2} opacity={0.85} />
              <line x1={4.8} y1={-8.3} x2={4.8} y2={7} stroke={SAND_300} strokeWidth={1.2} opacity={0.85} />
              <rect x={-1.7} y={-3.6} width={3.4} height={4.8} rx={0.9} fill={SAND_300} />
              <circle cx={0} cy={-1.5} r={0.75} fill="#072B37" />
            </g>
            {celebrating && (
              <g fill={SAND_100}>
                <path className="streak-spark streak-spark-a" d={sparkPath(-15, -12, 3.4)} />
                <path className="streak-spark streak-spark-b" d={sparkPath(15, -8, 2.6)} />
              </g>
            )}
          </g>
          {chestIsToday && (
            <text x={TREASURE_X} y={LABEL_Y} textAnchor="middle" fill={OCEAN_300} fontSize={9} fontStyle="italic">
              aujourd'hui
            </text>
          )}
        </g>
      </svg>

      {/* ——— trésor du palier : récompenses en icônes ——— */}
      <p className="mt-2.5 text-sm text-ocean-100 flex items-center gap-x-2 gap-y-1 flex-wrap">
        <span className={celebrating ? 'text-sand-300' : 'text-ocean-300'}>
          {celebrating ? `Trésor du ${frenchOrdinal(m.day)} jour décroché !` : `Trésor du ${frenchOrdinal(m.day)} jour :`}
        </span>
        {m.prestige > 0 && (
          <span className="inline-flex items-center gap-1 tabular-nums text-sand-200">
            +{m.prestige} <PrestigeIcon className="w-3.5 h-3.5 text-sand-300" />
          </span>
        )}
        {m.gold > 0 && (
          <span className="inline-flex items-center gap-1 tabular-nums text-sand-200">
            +{m.gold.toLocaleString('fr-FR')} <GoldIcon className="w-3.5 h-3.5 text-sand-300" />
          </span>
        )}
        {m.wood > 0 && (
          <span className="inline-flex items-center gap-1 tabular-nums text-sand-200">
            +{m.wood.toLocaleString('fr-FR')} <WoodIcon className="w-3.5 h-3.5 text-sand-300" />
          </span>
        )}
        {m.titleLabel && (
          <span className="text-sand-200">
            · titre <span className="font-display text-sand-300">« {m.titleLabel} »</span>
          </span>
        )}
      </p>

      {/* ——— jours d'escale en réserve ——— */}
      <p className="mt-1.5 text-sm text-ocean-100 flex items-center gap-2 flex-wrap" data-testid="streak-shore-leaves">
        <span className="text-ocean-300">Jours d'escale :</span>
        <span className="inline-flex items-center gap-1">
          {[0, 1].map((slot) => (
            <span key={slot} data-testid={slot < shoreLeaves ? 'shore-leave-full' : 'shore-leave-empty'} className="inline-flex">
              <LifebuoyIcon className={`w-4 h-4 ${slot < shoreLeaves ? 'text-ocean-300' : 'text-ocean-300/25'}`} />
            </span>
          ))}
        </span>
        <span className="text-xs text-ocean-300/80">
          un jour manqué est couvert automatiquement (1 escale gagnée tous les 7 jours, 2 max).
        </span>
      </p>

      {/* ——— le mot du bord ——— */}
      <p className="mt-2 text-sm italic text-ocean-300">
        {celebrating
          ? 'Trésor décroché : belle prise ! La série continue.'
          : todayDone
            ? 'Croix du jour tracée. Reviens demain.'
            : "Trace ta croix du jour ; n'importe quelle action suffit."}
      </p>
    </section>
  )
}
